import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { RestBuilder } from '../helpers/rest.builder';
import { ApiQueryOption } from '../../../../shared/constants/api';
import {
  IMatchComponent,
  IMatchComponentExistsQuery,
  IMatchComponentsResponse,
  IMatchComponentWithImage,
} from '../../../../shared/interfaces/match-component.interface';
import { IExistsResponse } from '../../../../shared/interfaces/common.interface';

@Injectable({
  providedIn: 'root',
})
export class MatchComponentApi {
  private restBuilder: RestBuilder;

  constructor(private httpClient: HttpClient) {
    this.restBuilder = new RestBuilder();
  }

  // GET /workspaces/:workspaceId/digital-properties/:digitalPropertyId/matchComponent/:matchComponentId
  public getMatchComponent(
    workspaceId: string,
    digitalPropertyId: string,
    componentId: string,
  ): Observable<IMatchComponentWithImage> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .matchComponents(componentId)
      .getApiUrl();

    return this.httpClient.get<IMatchComponentWithImage>(url, {});
  }

  // GET /workspaces/:workspaceId/digital-properties/:digitalPropertyId/matchComponent/:matchComponentId/impactful
  public getMatchComponentWithEngagementMetrics(
    workspaceId: string,
    digitalPropertyId: string,
    componentId: string,
  ): Observable<IMatchComponentWithImage> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .matchComponents(componentId)
      .impactful()
      .getApiUrl();

    return this.httpClient.get<IMatchComponentWithImage>(url, {});
  }

  // GET /workspaces/:workspaceId/digital-properties/:digitalPropertyId/matchComponent
  public getMatchComponents(
    workspaceId: string,
    digitalPropertyId: string,
    page: number = 0,
    limit: number = 0,
    sortBy?: string,
    direction?: string,
    term?: string,
  ): Observable<IMatchComponentsResponse> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .matchComponents()
      .getApiUrl();

    const params: { [param: string]: string | readonly string[] } = {};

    if (typeof limit === 'number') {
      params[ApiQueryOption.limit] = String(limit);
    }

    if (typeof page === 'number') {
      params[ApiQueryOption.page] = String(page);
    }

    if (typeof sortBy === 'string') {
      params[ApiQueryOption.sortBy] = sortBy;
    }

    if (typeof direction === 'string') {
      params[ApiQueryOption.direction] = direction;
    }

    if (term?.length > 0) {
      params[ApiQueryOption.term] = window.encodeURIComponent(term.trim());
    }

    const httpParams: HttpParams = new HttpParams({
      fromObject: params,
    });

    return this.httpClient.get<IMatchComponentsResponse>(url, { params: httpParams });
  }

  public getImpactfulMatchComponents(
    workspaceId: string,
    digitalPropertyId: string,
    page: number,
    limit: number,
  ): Observable<IMatchComponentsResponse> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .matchComponents()
      .impactful()
      .getApiUrl();

    const params: { [param: string]: string | readonly string[] } = {};
    params[ApiQueryOption.page] = String(page);
    params[ApiQueryOption.limit] = String(limit);

    const httpParams: HttpParams = new HttpParams({
      fromObject: params,
    });

    return this.httpClient.get<IMatchComponentsResponse>(url, { params: httpParams });
  }

  // POST /workspaces/:workspaceId/digital-properties/:digitalPropertyId/matchComponent
  public createMatchComponent(
    workspaceId: string,
    digitalPropertyId: string,
    matchComponent: FormData,
  ): Observable<IMatchComponent> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .matchComponents()
      .getApiUrl();

    return this.httpClient.post<IMatchComponent>(url, matchComponent);
  }

  // PUT /workspaces/:workspaceId/digital-properties/:digitalPropertyId/matchComponent/:matchComponentId
  public updateMatchComponent(
    workspaceId: string,
    digitalPropertyId: string,
    componentId: string,
    updateMatchComponent: FormData,
  ): Observable<IMatchComponentWithImage> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .matchComponents(componentId)
      .getApiUrl();

    return this.httpClient.put<IMatchComponentWithImage>(url, updateMatchComponent);
  }

  // PUT /workspaces/:workspaceId/digital-properties/:digitalPropertyId/matchComponent/:matchComponentId/archive
  public archiveMatchComponent(workspaceId: string, digitalPropertyId: string, matchComponentId: string): Observable<void> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .matchComponents(matchComponentId)
      .archive()
      .getApiUrl();

    return this.httpClient.post<void>(url, null);
  }

  // PUT /workspaces/:workspaceId/digital-properties/:digitalPropertyId/matchComponent/:matchComponentId/restore
  public restoreMatchComponent(workspaceId: string, digitalPropertyId: string, matchComponentId: string): Observable<void> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .matchComponents(matchComponentId)
      .restore()
      .getApiUrl();

    return this.httpClient.post<void>(url, null);
  }

  // GET /workspaces/:workspaceId/digital-properties/:digitalPropertyId/matchComponent/exists
  public exists(workspaceId: string, digitalPropertyId: string, query: IMatchComponentExistsQuery): Observable<IExistsResponse> {
    const queryParams: HttpParams = new HttpParams({
      fromObject: { ...query },
    });

    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .matchComponents()
      .exists()
      .getApiUrl();

    return this.httpClient.get<IExistsResponse>(url, { params: queryParams });
  }
}
